import {Dropdown, TextArea, TextInput, Toggle, Counter, Label} from '@/components/atoms'
import {SectionTitle} from '@/components/molecules'
import ImagePicker from '@/containers/Rewards/RewardSetup/ui-components/ImagePicker'
// no store interactions needed here
// Component now relies on useRewards hook for state/logic; no local state needed
import styles from './_rewardsDisplay.module.scss'
import {useRewards} from '@/hooks/useRewards'
import {ALERT_MIN, ALERT_MAX} from '@/utils/constants/rewardsDisplayConstants'
import { REWARD_TYPES } from '@/utils/constants/rewardsConstants'
import ToasterAlert from '@/components/atoms/Toast/toasterAlert'
import {COLOR_CODE} from '@/utils/constants/colors'
const RewardDisplay = () => {
  const {
    // data/state from hook
    languages,
    rewardDescription,
    display,
    productOptions,
    productChildList,
    completedCount,
    activeProductId,
    onProductChange,
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    alertDisplayCount,
    setAlertDisplayCount,
    shownOnHomepage,
    handleShownOnHomePageChange,
    imagesForActiveProduct,
    onImageSelectAt,
    updateRewardText,
    showToast,
    setShowToast,
    toastMessage,
    formData,
    updateGlobalRewardText,
    imageUpload,
    displaySingleImageUpload,
  } = useRewards()
  return (
    <div className={`flex flex-col gap-4 rounded-lg p-6 ${styles.rewardsDisplay} ${styles.rewardsDisplay__card}`}>
      <SectionTitle label={rewardDescription} color={COLOR_CODE._FFFFFF} className="w-full pb-0 pt-0" />

      <ToasterAlert
        type="warning"
        message={toastMessage}
        show={showToast}
        onCloseClick={() => setShowToast(!showToast)}
      />

      <div className="border-t border-t-[#3A3A3A]"></div>
      {productChildList?.length > 1 && (
        <div className="mb-4 mt-2 flex flex-row items-end space-x-10">
          <Dropdown
            className="dropdown"
            isJsonWrap={false}
            childList={productChildList}
            isDark={true}
            label="Product"
            placeholder={'Select Product'}
            selection="single"
            size="medium"
            style={{maxWidth: '425px'}}
            value={
              activeProductId
                ? [{id: activeProductId, displayText: productOptions.find(p => p.id === activeProductId)?.displayText}]
                : []
            }
            updateValue={onProductChange}
            showSelectedItems={true}
            isClearable={false}
          />
          <div className="mb-2 text-sm text-gray-50">
            {completedCount.count}/{completedCount.total} completed
          </div>
        </div>
      )}

      {!displaySingleImageUpload && <><Label
        className="text-[16px] font-bold text-white"
        label={'Reward Card'}
        showLabelIcon
        tooltipText="A minimum of 1 image is required. You may upload up to 3 images."
      />

      <div className="mt-2">
        <Toggle
          value={useDefaultImageToggleValue}
          updateValue={handleUseDefaultImageToggleChange}
          uiSchema={{
            label: 'Use Default Image',
            size: 'medium',
            variant: 'horizontal',
            isDark: true,
            className: '',
          }}
        />
      </div>
      {/* display / upload images section start */}
      {(() => {
        const images = imagesForActiveProduct
        const filled = images.filter(img => !!img.url)
        // If using default images, render only existing filled images for their actual positions
        // Otherwise render sequential positions with one extra slot for upload (up to 3)
        const positionsToRender = useDefaultImageToggleValue
          ? filled
              .slice()
              .sort((a, b) => Number(a?.position) - Number(b?.position))
              .map(img => Number(img?.position))
          : Array.from(
              {
                length: Math.min(3, (filled.length || 0) + (filled.length < 3 ? 1 : 0)),
              },
              (_, i) => i + 1
            )

        return (
          <div className="mt-2 flex flex-wrap gap-6">
            {positionsToRender.map(pos => (
              <div key={pos} style={{width: '380px'}}>
                <h3 className="my-1 text-[16px] font-medium">{`Display Position ${pos}`}</h3>

                <ImagePicker
                  onImageSelect={onImageSelectAt(pos)}
                  value={images.find(i => Number(i?.position) === pos)?.url}
                  label={pos === 1 ? 'Reward Image*' : 'Reward Image'}
                  allowedMimeTypes={['image/jpeg', 'image/png']}
                  invalidFormatsMessage={'File format is not valid. Supported Formats: PNG|JPG'}
                  disableDelete={useDefaultImageToggleValue}
                  disabled={useDefaultImageToggleValue}
                />
              </div>
            ))}
          </div>
        )
      })()}
      </>}
      {/* display / upload images section end */}
      <div className="my-4 flex flex-col">
        {formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS && (
          <>
            <Label className="text-[12px] text-[#dododo]" label={'Alert Display Count*'} />
            <Counter
              feedbackType="info"
              //label="Alert Display Count*"
              value={alertDisplayCount}
              onUpdate={setAlertDisplayCount}
              minValue={ALERT_MIN}
              maxValue={ALERT_MAX}
              size="medium"
              className="w-20"
              style={{width: '76px'}}
              disabled={false}
            />
          </>
        )}

        {
          displaySingleImageUpload && <div className="mt-2">
                <Label
                  className="text-[16px] font-bold text-white"
                  label={'Reward Card'}
                />
                <div key={1} style={{width: '380px'}}>
                  <ImagePicker
                    displaySingleImageUpload={displaySingleImageUpload}
                    onImageSelect={imageUpload()}
                    value={formData.imageUrls?.[0]}
                    label={'Reward Image*'}
                    allowedMimeTypes={['image/png', 'image/jpeg']}
                    invalidFormatsMessage={'File format is not valid. Supported Formats: PNG|JPG'}
                    disableDelete={useDefaultImageToggleValue}
                    disabled={useDefaultImageToggleValue}
                  />
                </div>
            </div>
        }

        <div className="mt-7">
          <Toggle
            value={shownOnHomepage}
            updateValue={handleShownOnHomePageChange}
            uiSchema={{
              label: 'Shown on Homepage',
              size: 'medium',
              variant: 'horizontal',
              isDark: true,
              className: '',
            }}
          />
        </div>
      </div>
      <div className="border-t border-t-[#3A3A3A]"></div>
      <SectionTitle label={"Reward Text"} color={COLOR_CODE._FFFFFF} className="w-full pb-0 pt-0" />

      <div className="flex flex-wrap gap-6">
        {languages.map(lang => {
          const languageName = lang.language
          const language = lang.code

          return (
            <div key={language} className="mt-0 space-y-5 font-semibold" style={{width: '380px'}}>
              <h3 className="font-bold text-[12px] ">{languageName}</h3>

              <TextInput
                key={`title-${activeProductId}-${language}`}
                showLabelIcon
                feedbackType="info"
                inputSize="medium"
                label="Title*"
                className="reward-display-text-input w-full text-[14px]"
                onChange={value => !displaySingleImageUpload ? updateRewardText(language, {title: value}) : updateGlobalRewardText(language, {title: value})}
                onUpdate={value => !displaySingleImageUpload ? updateRewardText(language, {title: value}) : updateGlobalRewardText(language, {title: value})}
                placeholderText={'Add Title'}
                value={ !displaySingleImageUpload ? display?.productDisplay?.[activeProductId]?.rewardText?.[language]?.title : formData.rewardText?.[language]?.title || ''}
                autocomplete={language === 'es_MX' ? 'off' : 'on'}
              />

              <TextArea
                key={`description-${activeProductId}-${language}`}
                label="Description*"
                maxLength={500}
                trim={false}
                updateValue={value => !displaySingleImageUpload ? updateRewardText(language, {description: value}) : updateGlobalRewardText(language, {description: value})}
                value={ !displaySingleImageUpload ? display?.productDisplay?.[activeProductId]?.rewardText?.[language]?.description : formData.rewardText?.[language]?.description || ''}
                uiSchema={{
                  helperText: '',
                  placeholder: 'Type here',
                  showToolbar: false,
                  className: 'reward-display-text-input w-full',
                  showCharacterCount: true,
                  maxLength: 500,
                }}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RewardDisplay
